import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Spinner,
  Text,
  Badge,
  Box,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import { appRequest } from "routes";

interface SelectMasterDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (document: any) => void;
  defaultSearch?: string;
  companyCode?: string;
  vendorCode?: string;
  slotType?: string; // "invoice", "agreement", "tax", "other"
}

const SelectMasterDocumentModal = ({
  isOpen,
  onClose,
  onSelect,
  defaultSearch = "",
  companyCode,
  vendorCode,
  slotType,
}: SelectMasterDocumentModalProps) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(defaultSearch);
  const [validTo, setValidTo] = useState(
    new Date().toISOString().split("T")[0],
  );
  const toast = useToast();

  const fetchData = async () => {
    try {
      setLoading(true);
      const payload: any = {
        page: 1,
        limit: 50, // Fetch a reasonable amount
        search: searchQuery,
      };

      if (companyCode) {
        payload.companyCode = companyCode;
      }
      if (vendorCode) {
        payload.vendorCode = vendorCode;
      }
      if (validTo) {
        payload.validateTo = validTo;
      }

      const response: any = await appRequest("documentMaster", "get", {
        ...payload,
        documentName: slotType,
      });

      if (response?.status === "success") {
        let activeDocs = (response.data || []).filter(
          (d: any) => d.isActive !== false,
        );

        if (slotType) {
        }

        setData(activeDocs);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast({
        title: "Error fetching documents",
        status: "error",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery(defaultSearch);
      setValidTo(new Date().toISOString().split("T")[0]);
      // We can let the debounced effect handle the fetch to avoid double call
      // BUT we need to ensure it runs immediately if there is a default search or just open
    }
  }, [isOpen]);

  // Debounced search effect - Handles both initial open (if query changes) and subsequent typing
  // To prevent double call on mount with defaultSearch, we can use a ref or just rely on this
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, validTo, isOpen]); // Added validTo to trigger on date change

  const handleSelect = (doc: any) => {
    if (!doc.file) {
      toast({
        title: "This entry has no file attached",
        status: "warning",
      });
      return;
    }
    onSelect(doc);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="900px">
        <ModalHeader>Select Document from Master Data</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex mb={4} gap={4} direction={{ base: "column", md: "row" }}>
            <Box flex="1">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by Vendor, Document Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Box>
            <Box w={{ base: "100%", md: "200px" }}>
              <Input
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                title="Filter by Valid To Date"
              />
            </Box>
          </Flex>

          <Box overflowX="auto">
            {loading ? (
              <Flex justify="center" p={8}>
                <Spinner />
              </Flex>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Document Name</Th>
                    <Th>Vendor</Th>
                    <Th>Company</Th>
                    <Th>Validity</Th>
                    <Th>Created By</Th>
                    <Th>Created At</Th>

                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.length > 0 ? (
                    data.map((row) => (
                      <Tr key={row._id} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="medium">{row.documentName}</Td>
                        <Td>
                          <Box>
                            <Text fontSize="xs" fontWeight="bold">
                              {row.vendorName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {row.vendorCode}
                            </Text>
                          </Box>
                        </Td>
                        <Td>{row.companyCode}</Td>
                        <Td fontSize="xs">
                          {row.validateFrom
                            ? new Date(row.validateFrom).toLocaleDateString()
                            : ""}{" "}
                          -
                          {row.validateTo
                            ? new Date(row.validateTo).toLocaleDateString()
                            : ""}
                        </Td>
                        <Td>{row.createdBy?.username || ""}</Td>
                        <Td>
                          {row.created_At
                            ? new Date(row.created_At).toLocaleDateString()
                            : ""}
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => handleSelect(row)}
                          >
                            Select
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={4}>
                        No documents found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SelectMasterDocumentModal;